import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'

interface Product {
  id: number
  sku: string | null
  name: string
  price: number
  category: string | null
  sessionCount: number | null
  active: boolean
  createdAt: string
}

type ListResponse = { products: Product[] }

type FormState = {
  id?: number
  sku: string
  name: string
  price: string
  category: string
  sessionCount: string
  active: boolean
}

const EMPTY: FormState = {
  sku: '',
  name: '',
  price: '',
  category: '',
  sessionCount: '',
  active: true,
}

export default function ProductsPage() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [editing, setEditing] = useState<FormState | null>(null)

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['products', 'all'],
    queryFn: () => api<ListResponse>('/products'),
  })

  const products = data?.products ?? []

  const categories = useMemo(
    () =>
      Array.from(
        new Set(products.map((p) => p.category).filter((c): c is string => !!c)),
      ).sort(),
    [products],
  )

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    return products.filter((p) => {
      if (!showInactive && !p.active) return false
      if (categoryFilter && p.category !== categoryFilter) return false
      if (query) {
        const hay = `${p.name} ${p.sku ?? ''} ${p.category ?? ''}`.toLowerCase()
        if (!hay.includes(query)) return false
      }
      return true
    })
  }, [products, q, categoryFilter, showInactive])

  const saveMut = useMutation({
    mutationFn: (f: FormState) => {
      const body = {
        sku: f.sku.trim() || null,
        name: f.name.trim(),
        price: Number(f.price),
        category: f.category.trim() || null,
        sessionCount: f.sessionCount === '' ? null : Number(f.sessionCount),
        active: f.active,
      }
      if (f.id) {
        return api<{ product: Product }>(`/products/${f.id}`, { method: 'PATCH', body })
      }
      return api<{ product: Product }>('/products', { method: 'POST', body })
    },
    onSuccess: () => {
      toastSuccess('บันทึกสำเร็จ')
      setEditing(null)
      qc.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      toastError(msg)
    },
  })

  const toggleMut = useMutation({
    mutationFn: (p: Product) =>
      api<{ product: Product }>(`/products/${p.id}`, {
        method: 'PATCH',
        body: { active: !p.active },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })

  function handleEdit(p: Product) {
    setEditing({
      id: p.id,
      sku: p.sku ?? '',
      name: p.name,
      price: String(p.price),
      category: p.category ?? '',
      sessionCount: p.sessionCount != null ? String(p.sessionCount) : '',
      active: p.active,
    })
  }

  function handleCreate() {
    setEditing({ ...EMPTY })
  }

  function handleSave() {
    if (!editing) return
    if (!editing.name.trim()) {
      toastError('กรุณากรอกชื่อสินค้า/บริการ')
      return
    }
    const price = Number(editing.price)
    if (!Number.isFinite(price) || price < 0) {
      toastError('ราคาไม่ถูกต้อง')
      return
    }
    saveMut.mutate(editing)
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <section className="card">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h1 className="text-xl font-semibold">สินค้า/บริการ</h1>
          <button className="btn-primary min-h-tap" onClick={handleCreate}>
            + เพิ่มรายการ
          </button>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="label">ค้นหา (ชื่อ / SKU / หมวด)</label>
            <input
              className="input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="เช่น Neuro, MINI-10"
            />
          </div>
          <div>
            <label className="label">หมวด</label>
            <select
              className="input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-600"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className="text-sm text-slate-700">แสดงที่ปิดอยู่</span>
          </label>
        </div>
      </section>

      <section className="card">
        {isLoading ? (
          <p className="text-slate-500 text-center py-8">กำลังโหลด…</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            ไม่พบรายการ —{' '}
            <button onClick={handleCreate} className="underline text-brand-700">
              เพิ่มรายการใหม่
            </button>
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 pl-2">ชื่อ</th>
                  <th className="py-2 w-28">SKU</th>
                  <th className="py-2 w-24">หมวด</th>
                  <th className="py-2 w-16 text-right">ครั้ง</th>
                  <th className="py-2 w-28 text-right">ราคา</th>
                  <th className="py-2 w-20 text-center">สถานะ</th>
                  <th className="py-2 w-28 text-right pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-t border-slate-100 ${
                      p.active ? '' : 'text-slate-400'
                    }`}
                  >
                    <td className="py-2 pl-2">
                      <div className="font-medium truncate max-w-xs">{p.name}</div>
                    </td>
                    <td className="py-2 font-mono text-xs">{p.sku ?? '—'}</td>
                    <td className="py-2">{p.category ?? '—'}</td>
                    <td className="py-2 text-right">{p.sessionCount ?? '—'}</td>
                    <td className="py-2 text-right font-medium">
                      {p.price.toLocaleString()}
                    </td>
                    <td className="py-2 text-center">
                      {p.active ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          เปิด
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          ปิด
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right pr-2 whitespace-nowrap">
                      <button
                        className="text-xs text-brand-700 hover:underline mr-3"
                        onClick={() => handleEdit(p)}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="text-xs text-slate-500 hover:text-rose-600 hover:underline"
                        onClick={() => toggleMut.mutate(p)}
                      >
                        {p.active ? 'ปิด' : 'เปิด'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editing.id ? 'แก้ไขรายการ' : 'เพิ่มรายการใหม่'}
              </h2>
              <button
                className="text-slate-400 hover:text-slate-600"
                onClick={() => setEditing(null)}
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="label">ชื่อ *</label>
                <input
                  className="input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div>
                <label className="label">ราคา *</label>
                <input
                  className="input text-right"
                  type="number"
                  min={0}
                  step={1}
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                />
              </div>
              <div>
                <label className="label">ครั้ง/จำนวน session</label>
                <input
                  className="input text-right"
                  type="number"
                  min={0}
                  step={1}
                  value={editing.sessionCount}
                  onChange={(e) =>
                    setEditing({ ...editing, sessionCount: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="label">หมวด</label>
                <input
                  className="input"
                  list="category-suggestions"
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                />
                <datalist id="category-suggestions">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="label">SKU</label>
                <input
                  className="input font-mono"
                  value={editing.sku}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-600"
                    checked={editing.active}
                    onChange={(e) =>
                      setEditing({ ...editing, active: e.target.checked })
                    }
                  />
                  <span className="text-sm text-slate-700">
                    เปิดใช้งาน (ให้แสดงในหน้า POS)
                  </span>
                </label>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                className="btn-primary flex-1"
                onClick={handleSave}
                disabled={saveMut.isPending}
              >
                {saveMut.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => setEditing(null)}
                disabled={saveMut.isPending}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
